import React, { useState } from 'react';
import { Plus, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import Header from '../../../components/Header';
import ComponentHeader from '../../../components/ComponentHeader';
import ShiftRoasterTable from '../../superAdmin/ShiftRoaster/ShiftRoasterTable';
import * as Yup from 'yup';
import { createShiftRoster } from '../../../services/shiftRoasterService';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import SuccessModal from '../../../components/ConfirmModal/SuccessModal';
import Cookies from 'js-cookie';
import ErrorModal from '../../../components/ConfirmModal/ErrorModal';

interface ShiftRosterFormValues {
  month: string;
  year: string;
}

const ShiftRoster = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const [showRosterModal, setShowRosterModal] = useState(false);
  const [reloadTable, setReloadTable] = useState(0);

  const shiftRosterSchema = Yup.object().shape({
    month: Yup.string().required('Month is required'),
    year: Yup.string().required('Year is required'),
  });
  const [successModal, setSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState({ title: '', message: '' });
  const [errorModal, setErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    title: '',
    message: '',
  });
  const handleCreateShiftRoster = async (values: ShiftRosterFormValues, { setSubmitting }: any) => {
    try {
      await createShiftRoster({
        month: Number(values.month),
        year: Number(values.year),
      });

      setShowRosterModal(false);
      setReloadTable((prev) => prev + 1);

      setSuccessMessage({
        title: 'Success',
        message: 'Shift roster created successfully!',
      });

      setSuccessModal(true);
    } catch (error: any) {
      const response = error?.response?.data;

      if (response?.errors?.month?.length > 0) {
        setErrorMessage({
          title: 'Shift Roster Already Exists',
          message: response.errors.month[0],
        });
      } else {
        setErrorMessage({
          title: 'Creation Failed',
          message: response?.message || 'Unable to create the shift roster. Please try again later.',
        });
      }

      setErrorModal(true);
    }
  };

  const role = Cookies.get('role');

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <Header title="Shift Roster" />

      <div className="flex-1 overflow-auto">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 px-6 mb-4">
          {role === 'shift_supervisor' && (
            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowRosterModal(true)}
                className="flex items-center gap-2 bg-[#e84545] text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-red-600"
              >
                <Plus size={16} />
                Add Shift Roster
              </button>

              <button
                onClick={() => navigate('/ShiftRoster/configuration')}
                className="flex items-center gap-2 border border-red-500 text-red-500 px-4 py-2 rounded-md text-sm font-medium hover:bg-red-50"
              >
                <Settings size={16} />
                Configuration
              </button>
            </div>
          )}

          <div className="sm:ml-auto">
            <ComponentHeader onSearch={setSearchQuery} />
          </div>
        </div>

        <ShiftRoasterTable reloadTable={reloadTable} searchQuery={searchQuery} />
      </div>

      {showRosterModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h2 className="text-xl font-semibold mb-4">Create Shift Roster</h2>

            <Formik<ShiftRosterFormValues>
              initialValues={{
                month: '',
                year: '',
              }}
              validationSchema={shiftRosterSchema}
              onSubmit={handleCreateShiftRoster}
            >
              {({ isSubmitting, status }) => (
                <Form>
                  {status && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{status}</div>}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">
                      Month <span className="text-red-500">*</span>
                    </label>

                    <Field as="select" name="month" className="w-full border rounded-md px-3 py-2">
                      <option value="">Select Month</option>
                      <option value="1">January</option>
                      <option value="2">February</option>
                      <option value="3">March</option>
                      <option value="4">April</option>
                      <option value="5">May</option>
                      <option value="6">June</option>
                      <option value="7">July</option>
                      <option value="8">August</option>
                      <option value="9">September</option>
                      <option value="10">October</option>
                      <option value="11">November</option>
                      <option value="12">December</option>
                    </Field>

                    <ErrorMessage name="month" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div className="mb-6">
                    <label className="block text-sm font-medium mb-1">
                      Year <span className="text-red-500">*</span>
                    </label>

                    <Field as="select" name="year" className="w-full border rounded-md px-3 py-2">
                      <option value="">Select Year</option>

                      {Array.from({ length: 10 }, (_, i) => {
                        const year = new Date().getFullYear() + i;

                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </Field>

                    <ErrorMessage name="year" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div className="flex justify-end gap-3">
                    <button type="button" onClick={() => setShowRosterModal(false)} className="px-4 py-2 border rounded-md">
                      Cancel
                    </button>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-[#e84545] text-white px-4 py-2 rounded-md flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed min-w-[90px]"
                    >
                      {isSubmitting && (
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                        </svg>
                      )}
                      {isSubmitting ? 'Creating...' : 'Create'}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}
      <SuccessModal
        isOpen={successModal}
        onClose={() => setSuccessModal(false)}
        title={successMessage.title}
        message={successMessage.message}
        buttonText="OK"
        onButtonClick={() => setSuccessModal(false)}
      />

      <ErrorModal
        isOpen={errorModal}
        onClose={() => setErrorModal(false)}
        title={errorMessage.title}
        message={errorMessage.message}
        buttonText="OK"
        onButtonClick={() => setErrorModal(false)}
      />
    </div>
  );
};

export default ShiftRoster;
